import { Box, FormControlProps, Text, useFormControlContext } from '@chakra-ui/react'
import { ReactNode, useCallback, useState } from 'react'

import { FormField } from '../FormField'
import FilePreviewList, { TFilePreview } from './FilePreviewList'
import { FILE_TYPES, TFileAccept } from './fileTypes'
import { useFilePicker } from './useFilePicker'

export interface IFileDropzoneProps extends Omit<FormControlProps, 'onChange' | 'label' | 'children'> {
  /** O que o campo aceita, por intenção. A lista de formatos e a dica vêm do registro. */
  accept: TFileAccept
  files: File[]
  /** Recebe a lista inteira já atualizada — os novos entram no fim. */
  onChange: (files: File[]) => void
  /** Tamanho máximo por arquivo, em MB. Espelhe o `max:` da regra do endpoint. */
  maxSizeMB?: number
  maxFiles?: number
  /** Soma máxima dos tamanhos, em MB — o limite do request, não o do arquivo. */
  maxTotalSizeMB?: number
  label?: ReactNode
  isRequired?: boolean
  isDisabled?: boolean
  error?: string | string[] | null
  /** Texto de apoio próprio do campo. A linha de formatos e limites é automática. */
  helperText?: ReactNode
  /** Forma da prévia dos arquivos escolhidos. */
  preview?: TFilePreview
  /** Primeira linha da área, para quando "Arraste arquivos ou clique aqui" não descreve o campo. */
  title?: string
}

interface IDropAreaProps {
  heading: string
  hint: string
  /** Se o campo tem rótulo. Sem ele o `FormField` não renderiza `<FormLabel>` nenhum. */
  hasLabel: boolean
  isDisabled?: boolean
  isProcessing: boolean
  onOpen: () => void
  onDropFiles: (files: FileList) => void
}

/**
 * A área de soltar, num componente próprio porque é o único jeito de ela **estar dentro** do
 * `FormControl` que o `FormField` monta — e é lá que o `useFormControlContext` existe.
 *
 * Sem isso o `<Box role="button">` era um controle desenhado à mão sem ligação nenhuma com o
 * campo: o `<FormLabel>` apontava (via `htmlFor`) para um id que não existia em elemento nenhum,
 * e nem o rótulo nem a mensagem de erro chegavam a quem usa leitor de tela — o campo era
 * anunciado só como "botão". O `WorkFilesDropzone` original tinha o mesmo furo; a diferença é que
 * agora ele estaria em oito telas.
 */
function DropArea({ heading, hint, hasLabel, isDisabled, isProcessing, onOpen, onDropFiles }: IDropAreaProps) {
  const field = useFormControlContext()
  const [isDraggingOver, setIsDraggingOver] = useState(false)

  const headingId = field?.id ? `${field.id}-heading` : undefined
  const hintId = field?.id ? `${field.id}-hint` : undefined

  // O `aria-labelledby` substitui o conteúdo como nome acessível, então o rótulo do campo e o
  // título da área entram juntos — senão um dos dois some do anúncio. O `labelId` só entra quando
  // existe rótulo: o `FormField` não renderiza `<FormLabel>` sem ele, e apontar para um id que não
  // existe é o mesmo furo, invertido.
  const labelledBy = [hasLabel ? field?.labelId : null, headingId].filter(Boolean).join(' ') || undefined
  const describedBy = [hintId, field?.hasFeedbackText ? field.feedbackId : null].filter(Boolean).join(' ') || undefined

  const isBlocked = isDisabled || isProcessing

  return (
    <Box
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      id={field?.id}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-invalid={field?.isInvalid || undefined}
      aria-required={field?.isRequired || undefined}
      aria-disabled={isBlocked || undefined}
      border="2px dashed"
      borderColor={isDraggingOver ? 'magenta.500' : field?.isInvalid ? 'red.500' : 'gray.300'}
      bg={isDraggingOver ? 'gray.50' : 'transparent'}
      borderRadius="md"
      p={6}
      textAlign="center"
      cursor={isBlocked ? 'not-allowed' : 'pointer'}
      opacity={isBlocked ? 0.6 : 1}
      transition="border-color 0.15s, background-color 0.15s"
      onClick={onOpen}
      onKeyDown={(event) => {
        if (isBlocked) return
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpen()
        }
      }}
      onDragOver={(event) => {
        event.preventDefault()
        if (!isBlocked) setIsDraggingOver(true)
      }}
      onDragLeave={() => setIsDraggingOver(false)}
      onDrop={(event) => {
        event.preventDefault()
        setIsDraggingOver(false)
        if (!isBlocked) onDropFiles(event.dataTransfer.files)
      }}
    >
      <Text id={headingId}>{heading}</Text>
      <Text variant="caption" id={hintId} mt={1}>
        {hint}
      </Text>
    </Box>
  )
}

/**
 * Campo de vários arquivos: área de arrastar e soltar, validação por arquivo e a lista do que foi
 * escolhido, com remoção.
 *
 * `WorkFilesDropzone` (entrega de trabalho) era o melhor upload do painel e nunca foi promovido —
 * era o único com drag & drop de verdade, feedback de arraste e recusa arquivo a arquivo. As
 * outras cinco telas que pedem vários arquivos tinham cada uma um pedaço disso: o anexo de suporte
 * validava tipo mas não tamanho (e anunciava um limite de 5MB que não existia no código), o
 * AbraMídia descartava o arquivo errado **sem dizer nada**, o reembolso usava `Promise.all` e
 * perdia os arquivos válidos junto com o inválido, e a prévia pública de entrega tinha uma área de
 * soltar sem `accept` e sem validação nenhuma.
 *
 * O campo inteiro passa pelo `FormField` — não embrulhe num `FormControl` de novo.
 */
export default function FileDropzone({
  accept,
  files,
  onChange,
  maxSizeMB,
  maxFiles,
  maxTotalSizeMB,
  label,
  isRequired,
  isDisabled,
  error,
  helperText,
  preview = 'list',
  title,
  ...rest
}: IFileDropzoneProps) {
  const onPick = useCallback((picked: File[]) => onChange([...files, ...picked]), [files, onChange])
  const { open, inputProps, handleFiles, isProcessing } = useFilePicker({
    accept,
    maxSizeMB,
    maxFiles,
    maxTotalSizeMB,
    files,
    multiple: true,
    isDisabled,
    onPick
  })

  const formats = FILE_TYPES[accept].label
  const limits = [maxSizeMB && `${maxSizeMB}MB por arquivo`, maxTotalSizeMB && `${maxTotalSizeMB}MB no total`].filter(
    Boolean
  )

  const heading = title ?? `Arraste arquivos ou clique aqui${maxFiles ? ` (máximo de ${maxFiles} arquivos)` : ''}`
  const hint = `Formatos aceitos: ${formats}.${limits.length ? ` Máximo de ${limits.join(' e ')}.` : ''}`

  return (
    <FormField label={label} error={error} isRequired={isRequired} isDisabled={isDisabled} {...rest}>
      <DropArea
        heading={heading}
        hint={hint}
        hasLabel={Boolean(label)}
        isDisabled={isDisabled}
        isProcessing={isProcessing}
        onOpen={open}
        onDropFiles={handleFiles}
      />
      <input {...inputProps} />

      {isProcessing && (
        <Text variant="caption" mt={2}>
          Preparando os arquivos...
        </Text>
      )}

      {files.length > 0 && (
        <Text variant="caption" mt={2}>
          {files.length} {maxFiles ? `de ${maxFiles} ` : ''}
          {files.length === 1 && !maxFiles ? 'arquivo selecionado' : 'arquivos selecionados'}
        </Text>
      )}

      <FilePreviewList
        files={files}
        variant={preview}
        isDisabled={isDisabled}
        onRemove={(index) => onChange(files.filter((_, i) => i !== index))}
      />

      {/* `Box` e não `Text`: o `helperText` é um `ReactNode` e pode trazer bloco — a tela de
          comprovantes passa um `<Flex>` com o spinner de leitura. Um `<div>` dentro do `<p>` que o
          `<Text>` renderiza é HTML inválido: dá aviso de `validateDOMNesting` e o browser fecha o
          parágrafo antes da hora, quebrando o layout. */}
      {helperText && (
        <Box fontSize="xs" color="gray.500" mt={1}>
          {helperText}
        </Box>
      )}
    </FormField>
  )
}
